import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { montarItensDoMenu } from '../menu-do-card';
import type { TicketStatus } from '../types';

interface Props {
  /** Posição do click (clientX/Y) — usada como hint inicial; o menu se reposiciona se sair da viewport. */
  x: number;
  y: number;
  /** Status do ticket — define quais ações aparecem (espelha os botões do header). */
  status: TicketStatus;
  /** Cabeçalho do menu — nome do contato. */
  title: string;
  /** Click fora, Esc, ou após escolher uma opção. */
  onDismiss: () => void;
  /** Aceitar conversa (pending). */
  onAccept: () => void;
  /** Retornar para fila (active → pending). */
  onQueue: () => void;
  onTransfer: () => void;
  onClose: () => void;
  /** Reabrir/retornar para fila (closed → pending). */
  onReopen: () => void;
  /** Conversa fixada no topo (aba Ativos). */
  pinned?: boolean | undefined;
  /** Fixar/desafixar — só na aba Ativos (é o ÚNICO gesto de pin: sem botão de
   *  hover no card, decisão do dono — gesto padronizado, passado no treinamento). */
  onTogglePin?: (() => void) | undefined;
  /** Devolver o selo de não-lido. Ausente quando o atendimento JÁ está não-lido
   *  (nada a fazer) — some do menu em vez de ficar lá sem efeito. */
  onMarkUnread?: (() => void) | undefined;
}

const MENU_WIDTH = 210;
const MENU_GUTTER = 8;

/**
 * Menu de contexto pra atalhos rápidos no card de atendimento (right-click).
 * Reproduz as ações do header do thread, variando por status:
 *  - pending: Aceitar conversa · Transferir · Marcar como não lida · Fechar
 *  - active:  Transferir · Retornar para Fila · Marcar como não lida ·
 *             Fixar conversa · Fechar
 *  - closed:  Retornar para Fila (reabrir)
 * Posiciona em `clientX/Y` e se ajusta pra não sair da viewport. Fecha em Esc,
 * click fora ou seleção de item.
 */
export function TicketContextMenu({
  x,
  y,
  status,
  title,
  onDismiss,
  onAccept,
  onQueue,
  onTransfer,
  onClose,
  onReopen,
  pinned,
  onTogglePin,
  onMarkUnread,
}: Props) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: x, top: y });

  // Reposiciona após render se sair da viewport (ex: click perto da borda direita).
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let left = x;
    let top = y;
    if (left + rect.width + MENU_GUTTER > window.innerWidth) {
      left = window.innerWidth - rect.width - MENU_GUTTER;
    }
    if (top + rect.height + MENU_GUTTER > window.innerHeight) {
      top = window.innerHeight - rect.height - MENU_GUTTER;
    }
    if (left !== pos.left || top !== pos.top) setPos({ left, top });
  }, [x, y, pos.left, pos.top]);

  // Fecha em Esc + click fora.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onDismiss();
    };
    const onClick = (e: MouseEvent): void => {
      const el = menuRef.current;
      if (el && !el.contains(e.target as Node)) onDismiss();
    };
    window.addEventListener('keydown', onKey);
    // Atrasa registro do click pra não capturar o próprio mouseup do contextmenu
    // que abriu o menu (causaria fechamento imediato em alguns browsers).
    const t = setTimeout(() => window.addEventListener('mousedown', onClick), 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(t);
      window.removeEventListener('mousedown', onClick);
    };
  }, [onDismiss]);

  // A composição e a ordem moram em `menu-do-card.ts` — aqui só o desenho.
  const items = montarItensDoMenu({
    status,
    onAccept,
    onQueue,
    onTransfer,
    onClose,
    onReopen,
    pinned,
    onTogglePin,
    onMarkUnread,
  });

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label={`Ações para ${title}`}
      className="fixed z-50 flex flex-col overflow-hidden rounded-md border border-n-200 bg-n-0 shadow-lg"
      style={{ left: pos.left, top: pos.top, width: MENU_WIDTH }}
    >
      <div className="border-n-200 border-b px-4 py-3 font-semibold text-n-900 text-sm">
        {title}
      </div>

      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="menuitem"
          onClick={() => {
            item.onClick();
            onDismiss();
          }}
          className={`flex w-full items-center gap-2 px-4 py-[12px] text-left text-sm transition ${
            item.danger ? 'text-danger-600 hover:bg-danger-50' : 'text-n-700 hover:bg-n-50'
          }`}
        >
          <item.icon size={16} strokeWidth={1.75} aria-hidden="true" />
          {item.label}
        </button>
      ))}
    </div>
  );
}
